import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { get, post, qs } from '../lib/api';
import { navigate, useDebounced, useLoad, useScanner } from '../lib/hooks';
import { useSession } from '../lib/session';
import { useCart } from '../lib/cart';
import { formatCents } from '../../shared/money';
import { buybackReceiptTitle } from '../../shared/receipts';
import { DEVICE_STATUS_LABELS, GRADE_LABELS, formatDateTime, formatPhone, formatTime, isValidImei, normalizePhone, zone } from '../../shared/format';
import { BUY_PAYOUTS, PAYOUT_LABELS, isAppPayout, payoutLabel, sellerPayoutLabel, type ImeiCheckSection, type Payout } from '../../shared/buyback';
import { checkFromSections, fillBlanks, findLine, phoneFromCheck, suggestFromCheck, type Suggestion } from '../../shared/sickw-phone';
import { Badge, Button, Confirm, Empty, Field, Money, MoneyInput, Sheet, Spinner, useAction, useToast, type ApprovalInput } from '../ui/kit';
import { Icon } from '../ui/icons';
import { Barcode, usePrint, type PaperSize } from '../ui/print';
import { useCloudPrint } from '../lib/printing';
import { ReceiptMark } from '../ui/legal';
import { SignaturePad } from '../ui/signature';
import { photoToDataUrl, pickPhoto } from '../lib/image';
import { scanThumbprint } from '../lib/fingerprint';
import { ModelPhoto } from '../ui/photo';
import { ReceiptHeader, ReceiptPolicies, SiteLine, storeLines, type StoreInfo } from './Receipt';
import { EasyBuyButton } from './EasyBuy';
import { EditManySheet } from './Inventory';
import { PickAll } from '../ui/bulk';
import { PhoneCheckButton } from './PhoneCheck';
import { UndoWholesaleSheet, WHOLESALE_STATUSES, WholesaleInfo, WholesaleSheet } from './Wholesale';
import { printPriceStickers } from './PriceSticker';
import { LicenseSummary, ScanIdSheet, todayIso, usDate } from './ScanId';
import { useLicenseScanner } from '../lib/license-scan';
import { ageOn, isExpired, type LicenseData } from '../../shared/license';

const ID_TYPES = ['Driver license', 'State ID', 'Passport', 'Military ID', 'Other government ID'];
type Tone = 'green' | 'red' | 'amber' | 'plain';
const CHECK: Record<string, { label: string; tone: Tone }> = {
  clean: { label: 'Clean', tone: 'green' },
  blocked: { label: 'Blacklisted, lost or stolen', tone: 'red' },
  fmi_on: { label: 'Find My / iCloud is ON', tone: 'red' },
  unknown: { label: 'Read the result', tone: 'amber' },
  error: { label: 'The check didn’t work', tone: 'amber' },
  not_set_up: { label: 'IMEI checks not set up', tone: 'plain' },
  not_checked: { label: 'Not checked', tone: 'amber' },
};

export const PAY_NOTE: Record<string, string> = {
  blue_bag: 'Pay with the buyback money in the blue bag.',
  cash: 'For emergencies only: the cash comes out of the register drawer.',
  zelle: 'Send it by Zelle, then add the confirmation number.',
  cashapp: 'Send it by Cash App, then add the payment ID.',
  apple_pay: 'Send it by Apple Pay (Apple Cash), then add a note.',
};

// ---------------------------------------------------------------- SICKW results

/** Colors a SICKW line: clean / off / unlocked are good, blacklisted / on are not. */
function lineTone(label: string, value: string): Tone {
  const l = label.toLowerCase();
  const v = value.toLowerCase();
  if (/blacklist|lost|stolen|barred|gsma/.test(l)) return /\b(clean|no|not reported|not blacklisted|unbarred)\b/.test(v) ? 'green' : /blacklist|lost|stolen|barred|blocked|yes|reported/.test(v) ? 'red' : 'amber';
  if (/icloud|find my|fmi|activation lock/.test(l)) return /\boff\b|\bclean\b/.test(v) ? 'green' : /\bon\b|\blost\b/.test(v) ? 'red' : 'plain';
  if (/sim.?lock/.test(l)) return /unlock/.test(v) ? 'green' : /lock/.test(v) ? 'amber' : 'plain';
  if (/mdm|knox|demo|replaced|loaner/.test(l)) return /\b(off|no|clean)\b/.test(v) ? 'green' : /\b(on|yes|active|locked|enrolled)\b/.test(v) ? 'red' : 'plain';
  return 'plain';
}

/** The few lines that decide a buy, shown as badges above the full list. */
function highlights(sections: ImeiCheckSection[]): { label: string; value: string; tone: Tone }[] {
  const picks: [RegExp, string][] = [[/blacklist/i, 'Blacklist'], [/icloud|find my|fmi/i, 'iCloud'], [/sim.?lock/i, 'SIM-Lock'], [/^(locked carrier|carrier)$/i, 'Carrier']];
  const out: { label: string; value: string; tone: Tone }[] = [];
  for (const [re, label] of picks) {
    const f = findLine(sections, re);
    if (f) out.push({ label, value: f[1], tone: lineTone(f[0], f[1]) });
  }
  return out;
}

export function ImeiCheckView({ check, showText = true }: { check: any; showText?: boolean }) {
  const sections: ImeiCheckSection[] = check?.sections ?? [];
  const meta = CHECK[check?.status] ?? { label: String(check?.status ?? ''), tone: 'plain' as Tone };
  const hl = highlights(sections);
  return (
    <div className={`imei-result tone-${meta.tone}`} aria-label="IMEI check result">
      <div className="row-wrap">
        <Badge tone={meta.tone}>{meta.label}</Badge>
        {hl.map((h) => <Badge key={h.label} tone={h.tone}>{h.label}: {h.value}</Badge>)}
        <span className="grow" />
        {check?.checked_at ? <span className="small muted">{check.cached ? 'Checked earlier today (not charged again) · ' : ''}{formatDateTime(check.checked_at)}</span> : null}
      </div>
      {sections.length ? sections.map((sec) => (
        <div key={sec.service} className="imei-sec">
          <div className="small strong">{sec.title} <span className="muted">· SICKW #{sec.service}</span></div>
          {sec.ok ? (
            <dl className="imei-grid">
              {sec.fields.map(([k, v], i) => (k ? (
                <Fragment key={i}><dt>{k}</dt><dd className={`v-${lineTone(k, v)}`}>{v}</dd></Fragment>
              ) : <dd key={i} className="full">{v}</dd>))}
            </dl>
          ) : <div className="error-text small">{sec.error}</div>}
        </div>
      )) : showText && check?.text ? <pre className="small" style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>{check.text}</pre> : null}
      {check?.balance ? <div className="small muted">SICKW balance: ${check.balance}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- buying

interface KnownCustomer { id: string; name: string; phone: string | null; buybacks: number; address1: string | null; city: string | null; state: string | null; zip: string | null; store_credit_cents: number }

/**
 * Everything filled in on Buy a phone or Trade-in, kept on this tablet as it's typed, so leaving
 * the screen (or the app) and coming back picks up where it was. Cleared when it's saved, when
 * someone taps Start over, or after a day.
 */
interface BuyDraft {
  at: number;
  requestId: string;
  custName: string; custPhone: string; known: KnownCustomer | null; email: string;
  brand: string; model: string; storage: string; carrier: string; locked: boolean; color: string; code: string; check: any;
  condition: string; answers: Record<string, string>; est: any; offer: number | null;
  seller: { name: string; address: string; city: string; state: string; zip: string; dob: string };
  showId: boolean; idDoc: { type: string; number: string; state: string; expires: string };
  photos: { kind: 'device' | 'id'; dataUrl: string }[]; age: boolean;
  payout: Payout; payoutRef: string; note: string; signature: string | null; thumb: string | null; scanned: LicenseData | null;
  /** Staff picked the carrier themselves: an IMEI check doesn't change it. */
  carrierPicked?: boolean;
  /** IMEIs the check already ran for by itself on this form (it never runs twice on its own). */
  autoChecked?: string[];
}
const DRAFT_MS = 24 * 3_600_000;
const draftKey = (trade: boolean) => `sos_${trade ? 'trade' : 'buy'}_draft`;
// The latest draft with its pictures while the app is open (pictures may not all fit in storage).
const draftsInMemory: Record<string, BuyDraft | undefined> = {};

function loadDraft(trade: boolean): BuyDraft | null {
  const key = draftKey(trade);
  let d = draftsInMemory[key] ?? null;
  if (!d) {
    try {
      const raw = localStorage.getItem(key);
      d = raw ? (JSON.parse(raw) as BuyDraft) : null;
    } catch {
      d = null;
    }
  }
  if (!d || typeof d.at !== 'number' || Date.now() - d.at > DRAFT_MS) {
    clearDraft(trade);
    return null;
  }
  return d;
}

function saveDraft(trade: boolean, d: BuyDraft): void {
  const key = draftKey(trade);
  draftsInMemory[key] = d;
  try {
    localStorage.setItem(key, JSON.stringify(d));
  } catch {
    // Too big with the pictures: keep the typing (the pictures stay while the app is open).
    try { localStorage.setItem(key, JSON.stringify({ ...d, photos: [], thumb: null, signature: null })); } catch { /* storage off */ }
  }
}

function clearDraft(trade: boolean): void {
  const key = draftKey(trade);
  delete draftsInMemory[key];
  try { localStorage.removeItem(key); } catch { /* storage off */ }
}

/** Whether anything worth keeping is filled in. */
const draftHasContent = (d: BuyDraft) => !!(d.custName.trim() || d.custPhone.trim() || d.email.trim() || d.model.trim() || d.code.trim() || d.condition
  || d.offer || d.photos.length || d.signature || d.thumb || d.idDoc.number.trim() || d.seller.name.trim() || d.seller.address.trim() || d.note.trim() || d.payoutRef.trim());

/**
 * Buy a phone from a customer, or take it as a trade-in (store credit toward a sale). Both start
 * with the customer's name and phone. Buying takes the seller's signature and thumbprint (the ID
 * section is there, but nothing in it is required). A trade-in only needs the name and phone
 * (email and ID optional, no signature); the value comes off the phone they buy in Sale.
 */
export function BuyScreen({ trade, query }: { trade: boolean; query?: URLSearchParams }) {
  // "Start over" gives a fresh, empty form.
  const [round, setRound] = useState(0);
  return <BuyForm key={`${trade ? 'trade' : 'buy'}-${round}`} trade={trade} query={query} onStartOver={() => { clearDraft(trade); setRound((n) => n + 1); }} />;
}

function BuyForm({ trade, query, onStartOver }: { trade: boolean; query?: URLSearchParams; onStartOver: () => void }) {
  // A trade-in started from a sale (the phone they're buying): saving goes back to the sale.
  const forSale = trade && query?.get('for') === 'sale';
  // What was filled in before, on this tablet (left the screen, or the app closed).
  const [d0] = useState(() => loadDraft(trade));
  const restoredAt = d0 && draftHasContent(d0) ? d0.at : null;
  const { me } = useSession();
  const toast = useToast();
  const cart = useCart();
  const { run, busy } = useAction();
  const cat = useLoad<any>(() => get('/api/buyback/catalog'), []);
  // 1. Customer
  const [custName, setCustName] = useState(d0?.custName ?? '');
  const [custPhone, setCustPhone] = useState(d0?.custPhone ?? '');
  const [known, setKnown] = useState<KnownCustomer | null>(d0?.known ?? null);
  // Typing the name searches the customers on file; picking one fills everything in.
  const [nameOpen, setNameOpen] = useState(false);
  const [nameHits, setNameHits] = useState<any[]>([]);
  // One id for this form: pressing the button twice (or a retry) never gives credit twice.
  const requestId = useRef(d0?.requestId ?? `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`);
  // 2. Phone
  const [brand, setBrand] = useState(d0?.brand ?? 'Apple');
  const [model, setModel] = useState(d0?.model ?? '');
  const [storage, setStorage] = useState(d0?.storage ?? '');
  const [carrier, setCarrier] = useState(d0?.carrier ?? 'Unlocked');
  const [locked, setLocked] = useState(d0?.locked ?? false);
  const [color, setColor] = useState(d0?.color ?? '');
  const [code, setCode] = useState(d0?.code ?? '');
  const [check, setCheck] = useState<any>(d0?.check ?? null);
  const [checking, setChecking] = useState(false);
  // Staff picked the carrier themselves: a check doesn't change it (Unlocked is only where it starts).
  const [carrierPicked, setCarrierPicked] = useState(d0?.carrierPicked ?? (!!d0 && d0.carrier !== 'Unlocked'));
  // What the IMEI check filled in, shown so it can be corrected; or the brand it says the phone is.
  const [filled, setFilled] = useState<{ what: string[]; otherBrand?: string; model?: string } | null>(null);
  // IMEIs the check already ran for by itself on this form: SICKW is never asked twice on its own.
  const autoChecked = useRef<Set<string>>(new Set(d0?.autoChecked ?? []));
  const checkInFlight = useRef<string | null>(null);
  // 3. Condition and offer
  const [condition, setCondition] = useState(d0?.condition ?? '');
  const [answers, setAnswers] = useState<Record<string, string>>(d0?.answers ?? {});
  const [est, setEst] = useState<any>(d0?.est ?? null);
  const [estimating, setEstimating] = useState(false);
  const [offer, setOffer] = useState<number | null>(d0?.offer ?? null);
  // Trade-in: the customer's email (optional).
  const [email, setEmail] = useState(d0?.email ?? '');
  // 4. Seller's ID (nothing here is required). For a trade-in it's folded away until staff add it.
  const [seller, setSeller] = useState(d0?.seller ?? { name: '', address: '', city: '', state: 'CA', zip: '', dob: '' });
  const [showId, setShowId] = useState(d0?.showId ?? !trade);
  const [idDoc, setIdDoc] = useState(d0?.idDoc ?? { type: 'Driver license', number: '', state: 'CA', expires: '' });
  const [photos, setPhotos] = useState<{ kind: 'device' | 'id'; dataUrl: string }[]>(d0?.photos ?? []);
  const [age, setAge] = useState(d0?.age ?? false);
  // 5. Payment
  const [payout, setPayout] = useState<Payout>(d0?.payout ?? (trade ? 'store_credit' : 'blue_bag'));
  const [payoutRef, setPayoutRef] = useState(d0?.payoutRef ?? '');
  const [note, setNote] = useState(d0?.note ?? '');
  // 6. Signature and thumbprint
  const [signature, setSignature] = useState<string | null>(d0?.signature ?? null);
  const [thumb, setThumb] = useState<string | null>(d0?.thumb ?? null);
  const [scanning, setScanning] = useState(false);
  const [scanErr, setScanErr] = useState<string | null>(null);
  // Scanning their license fills in the customer and the ID.
  const [scanOpen, setScanOpen] = useState(false);
  const [scanned, setScanned] = useState<LicenseData | null>(d0?.scanned ?? null);
  const autoName = useRef('');
  const autoPhone = useRef('');

  const brands: string[] = useMemo(() => Object.keys(cat.data?.brands ?? {}).length ? Object.keys(cat.data.brands) : ['Apple', 'Samsung', 'Google'], [cat.data]);
  const models: string[] = useMemo(() => Object.keys(cat.data?.brands?.[brand] ?? {}), [cat.data, brand]);
  const storages: string[] = cat.data?.brands?.[brand]?.[model] ?? [];
  const carriers: string[] = cat.data?.carriers ?? ['Unlocked'];
  const dm = useDebounced(model.trim().toLowerCase(), 150);
  const suggestions = useMemo(() => {
    if (models.some((m) => m.toLowerCase() === dm)) return [];
    return models.filter((m) => !dm || m.toLowerCase().includes(dm)).slice(0, 14);
  }, [models, dm]);

  // A new phone or condition means the old offer no longer applies (not when the form first
  // opens with what was filled in before).
  const opening = useRef(true);
  useEffect(() => { if (!opening.current) { setEst(null); setOffer(null); } }, [brand, model, storage, carrier, locked, condition, JSON.stringify(answers)]);
  useEffect(() => { if (!opening.current) { setCheck(null); setFilled(null); } }, [code, brand]);
  useEffect(() => { opening.current = false; }, []);

  // Keep everything filled in, as it's typed, on this tablet.
  const draft: BuyDraft = {
    at: Date.now(), requestId: requestId.current, custName, custPhone, known, email, brand, model, storage, carrier, locked, color, code, check,
    condition, answers, est, offer, seller, showId, idDoc, photos, age, payout, payoutRef, note, signature, thumb, scanned,
    carrierPicked, autoChecked: [...autoChecked.current],
  };
  const latest = useRef(draft);
  latest.current = draft;
  const finished = useRef(false);
  useEffect(() => {
    const t = setTimeout(() => {
      if (finished.current) return;
      if (draftHasContent(latest.current)) saveDraft(trade, latest.current);
      else clearDraft(trade);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custName, custPhone, known, email, brand, model, storage, carrier, locked, color, code, check, condition, answers, est, offer, seller, showId, idDoc, photos, age, payout, payoutRef, note, signature, thumb, scanned, carrierPicked]);
  // Leaving the screen: keep the very last thing typed too.
  useEffect(() => () => {
    if (finished.current) return;
    if (draftHasContent(latest.current)) saveDraft(trade, latest.current);
  }, [trade]);

  // A returning customer is found by their phone number: their name and address come back.
  const phone10 = normalizePhone(custPhone);
  useEffect(() => {
    let live = true;
    if (!phone10) { setKnown(null); return; }
    if (known?.phone === phone10) return;
    get<any[]>(`/api/customers${qs({ q: phone10, limit: 5 })}`)
      .then(async (hits) => {
        const hit = hits.find((h) => h.phone === phone10);
        if (!live) return;
        if (!hit) { setKnown(null); return; }
        const full = await get(`/api/customers/${hit.id}`).catch(() => null);
        if (!live) return;
        const k: KnownCustomer = {
          id: hit.id, name: hit.name, phone: hit.phone, buybacks: (full?.stats?.buybacks ?? 0) + (full?.stats?.trade_ins ?? 0),
          address1: full?.address1 ?? null, city: full?.city ?? null, state: full?.state ?? null, zip: full?.zip ?? null, store_credit_cents: hit.store_credit_cents ?? 0,
        };
        setKnown(k);
        setCustName((n) => n.trim() ? n : k.name);
        setSeller((x) => (x.address || x.city || x.zip ? x : { ...x, address: k.address1 ?? '', city: k.city ?? '', state: k.state ?? x.state, zip: k.zip ?? '' }));
      })
      .catch(() => undefined);
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone10]);

  // Customers on file whose name (or phone) matches what's typed in the name box.
  const dn = useDebounced(custName.trim(), 200);
  useEffect(() => {
    let live = true;
    if (dn.length < 2 || (known && known.name.toLowerCase() === dn.toLowerCase())) { setNameHits([]); return; }
    get<any[]>(`/api/customers${qs({ q: dn, limit: 6 })}`).then((h) => live && setNameHits(h)).catch(() => undefined);
    return () => { live = false; };
  }, [dn, known]);
  const chooseCustomer = async (id: string) => {
    setNameOpen(false);
    setNameHits([]);
    const full = await get<any>(`/api/customers/${id}`).catch(() => null);
    if (!full) return;
    const k: KnownCustomer = {
      id: full.id, name: full.name, phone: full.phone, buybacks: (full.stats?.buybacks ?? 0) + (full.stats?.trade_ins ?? 0),
      address1: full.address1 ?? null, city: full.city ?? null, state: full.state ?? null, zip: full.zip ?? null, store_credit_cents: full.store_credit_cents ?? 0,
    };
    setKnown(k);
    setCustName(k.name);
    if (k.phone) setCustPhone(formatPhone(k.phone));
    if (full.email) setEmail((e) => e || full.email);
    setSeller((x) => (x.address || x.city || x.zip ? x : { ...x, address: k.address1 ?? '', city: k.city ?? '', state: k.state ?? x.state, zip: k.zip ?? '' }));
  };

  useEffect(() => {
    const c = cart.cart.customer;
    if (!forSale || !c) return;
    setCustName((n) => n || c.name);
    if (c.phone) setCustPhone((p) => p || formatPhone(c.phone));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  /** A scanned license: the name, ID number, date of birth and address; a returning seller's phone. */
  const applyLicense = (d: LicenseData) => {
    setScanOpen(false);
    const sameId = scanned?.number === d.number;
    setScanned(d);
    const before = autoName.current;
    autoName.current = d.name;
    setCustName((n) => (n.trim() && n.trim() !== before ? n : d.name));
    // Another person's license than the last scan: the phone that scan brought in goes too.
    let phoneEmpty = !phone10;
    if (!sameId && autoPhone.current && phone10 === autoPhone.current) {
      setCustPhone('');
      setKnown(null);
      autoPhone.current = '';
      phoneEmpty = true;
    }
    setShowId(true);
    setIdDoc((x) => ({ ...x, type: d.kind === 'ID' ? 'State ID' : 'Driver license', number: d.number, state: d.state || x.state, expires: d.expires ?? '' }));
    setSeller((x) => ({ ...x, name: d.fullName, dob: d.dob ?? x.dob, address: d.address || x.address, city: d.city || x.city, state: d.addressState || x.state, zip: d.zip || x.zip }));
    toast(`Read ${d.name}’s ${d.kind === 'ID' ? 'ID' : 'license'}.`, 'good');
    if (phoneEmpty) {
      get<{ result: { customer: { phone: string | null } } | null }>(`/api/buyback/seller${qs({ idNumber: d.number, state: d.state || undefined })}`)
        .then((r) => {
          const ph = r.result?.customer.phone;
          if (!ph) return;
          autoPhone.current = normalizePhone(ph) ?? '';
          setCustPhone((p) => (normalizePhone(p) ? p : formatPhone(ph)));
        })
        .catch(() => undefined);
    }
  };
  useLicenseScanner(applyLicense, () => toast('That license didn’t read. Scan it again, or press Scan ID.', 'error'), !scanOpen);

  const digits = code.replace(/\D/g, '');
  const isImei = digits.length === 15 && digits === code.trim();
  const imeiBad = isImei && !isValidImei(digits);
  const sickwSays = useMemo(
    () => (check?.sections ? suggestFromCheck(check.sections, models, (m) => cat.data?.brands?.[brand]?.[m] ?? [], carriers) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [check, models, brand, cat.data],
  );
  const applySuggestion = (s: Suggestion) => {
    if (s.model) setModel(s.model);
    if (s.storage) setStorage(s.storage); else if (s.model && s.model !== model) setStorage('');
    if (s.color) setColor(s.color);
    if (s.carrier) { setCarrier(s.carrier); setLocked(s.carrier !== 'Unlocked' && (s.locked ?? locked)); }
  };
  const differs = !!sickwSays && ((!!sickwSays.model && sickwSays.model !== model) || (!!sickwSays.storage && sickwSays.storage !== storage)
    || (!!sickwSays.carrier && (sickwSays.carrier !== carrier || (sickwSays.locked !== undefined && sickwSays.carrier !== 'Unlocked' && sickwSays.locked !== locked))));

  /**
   * What the check says the phone is goes into the boxes still empty (and the carrier until staff
   * pick one), never over what staff typed. A check saying it's another brand's phone fills nothing.
   */
  const fillFromCheck = (r: any, id: string, forBrand: string) => {
    const sections: ImeiCheckSection[] = r?.sections ?? [];
    if (!sections.some((s) => s.ok)) { setFilled(null); return; }
    const found = phoneFromCheck(checkFromSections(id, sections), { brands: cat.data?.brands ?? {}, carriers });
    const cur = latest.current;
    const f = fillBlanks(
      { brand: forBrand, model: cur.model, storage: cur.storage, color: cur.color, carrier: cur.carrier, carrierPicked: !!cur.carrierPicked },
      found, (m) => cat.data?.brands?.[forBrand]?.[m] ?? [], brands,
    );
    if (f.otherBrand) { setFilled({ what: [], otherBrand: f.otherBrand, model: found.model }); return; }
    if (f.model) setModel(f.model);
    if (f.storage) setStorage(f.storage);
    if (f.color) setColor(f.color);
    if (f.carrier) { setCarrier(f.carrier); setLocked(!!f.locked); }
    setFilled(f.what.length ? { what: f.what } : null);
  };
  const checkImei = async (opts: { fresh?: boolean; brand?: string } = {}) => {
    const id = code.trim();
    const forBrand = opts.brand ?? brand;
    const key = `${id}|${forBrand}|${opts.fresh ? 'fresh' : ''}`;
    // The same check is already on its way (a scanner's Enter right after the IMEI started it).
    if (checkInFlight.current === key) return;
    checkInFlight.current = key;
    setChecking(true);
    try {
      const r = await post('/api/buyback/imei-check', { imei: id, brand: forBrand, fresh: !!opts.fresh });
      // The IMEI or brand changed while SICKW was answering: this answer is for another phone.
      if (latest.current.code.trim() !== id || latest.current.brand !== forBrand) return;
      setCheck(r);
      fillFromCheck(r, id, forBrand);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'The check didn’t work.', 'error');
    } finally {
      if (checkInFlight.current === key) {
        checkInFlight.current = null;
        setChecking(false);
      }
    }
  };
  // Enter runs the check for a serial, or an IMEI not checked yet. A full IMEI checks by itself, and
  // a scanner presses Enter right after it: that Enter doesn't ask SICKW again.
  const imeiKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (code.trim().length >= 11 && !imeiBad && !check && !checking && !(isImei && autoChecked.current.has(digits))) void checkImei();
  };
  // SICKW says it's another brand's phone: check it again as that brand (staff press it).
  const switchBrand = (b: string) => {
    setBrand(b);
    setModel('');
    setStorage('');
    setFilled(null);
    void checkImei({ brand: b });
  };
  // A full, valid IMEI scanned or typed: the check that fits the brand runs by itself, once per IMEI
  // on this form. The server shows a check from earlier today instead of paying SICKW again.
  useEffect(() => {
    if (!isImei || imeiBad || check || checking || autoChecked.current.has(digits)) return;
    autoChecked.current.add(digits);
    void checkImei();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits, isImei, imeiBad, check, checking]);
  // A phone's IMEI scanned with no box in use (from its box, or the screen at *#06#): it goes in the IMEI box.
  useScanner((raw) => {
    const n = raw.replace(/\D/g, '');
    if (n.length === 15 && isValidImei(n)) setCode(n);
  }, !scanOpen);

  // The website offer is asked for by itself once the phone and its grade are picked (asking is
  // free): staff only check the amount. A model typed by hand is asked once it's one of the website's.
  const offerKeyOf = (x: Pick<BuyDraft, 'brand' | 'model' | 'storage' | 'carrier' | 'locked' | 'condition' | 'answers'>) =>
    JSON.stringify([x.brand, x.model.trim(), x.storage, x.carrier, x.locked, x.condition, x.answers]);
  const offerKey = offerKeyOf(draft);
  const askedFor = useRef<string | null>(d0?.est ? offerKey : null);
  const estimate = async (auto = false) => {
    const key = offerKey;
    setEstimating(true);
    try {
      const e = await post('/api/buyback/estimate', { brand, model: model.trim(), storage: storage || null, carrier, locked, condition, answers });
      // The phone or grade changed while the website answered: this offer is for another phone.
      if (offerKeyOf(latest.current) !== key) return;
      setEst(e);
      // Asked for by itself: an amount staff already typed stays.
      if (e.offer_cents && (!auto || latest.current.offer === null)) setOffer(e.offer_cents);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Couldn’t get the offer.', 'error');
    } finally {
      setEstimating(false);
    }
  };
  useEffect(() => {
    if (est || estimating || !condition || !models.includes(model.trim()) || (storages.length > 0 && !storage)) return;
    if (askedFor.current === offerKey) return;
    const t = setTimeout(() => { askedFor.current = offerKey; void estimate(true); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerKey, est, estimating, models.length, storages.length]);
  const addPhoto = async (kind: 'device' | 'id') => {
    const f = await pickPhoto({ camera: true });
    if (!f) return;
    try {
      const dataUrl = await photoToDataUrl(f, 1400, 0.8);
      setPhotos((p) => [...p, { kind, dataUrl }].slice(-6));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That photo can’t be used.', 'error');
    }
  };
  const scanThumb = async () => {
    setScanning(true);
    setScanErr(null);
    try {
      const r = await scanThumbprint({ license: me?.settings?.fingerprint_license || undefined });
      setThumb(r.dataUrl);
      toast('Thumbprint scanned.', 'good');
    } catch (e) {
      setScanErr(e instanceof Error ? e.message : 'The scanner didn’t work.');
    } finally {
      setScanning(false);
    }
  };
  const uploadThumb = async () => {
    const f = await pickPhoto();
    if (!f) return;
    try {
      setThumb(await photoToDataUrl(f, 1000, 0.92));
      setScanErr(null);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That picture can’t be used.', 'error');
    }
  };

  // An ID folded away on a trade-in isn't sent.
  const idNo = showId ? idDoc.number.trim() : '';
  const missing = [
    custName.trim().length < 2 && 'customer name',
    !phone10 && 'phone number',
    trade && !!email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) && 'a valid email',
    !model.trim() && 'model',
    !(isImei && !imeiBad) && !(code.trim() && !isImei && code.trim().length >= 5) && 'IMEI or serial',
    !condition && 'grade',
    !(offer && offer > 0) && 'amount',
    idNo && idNo.length < 4 && 'the full ID number',
  ].filter(Boolean) as string[];

  const save = async () => {
    if (missing.length) return;
    // The ID details go on the record when they're open (always, when buying); a trade-in
    // isn't signed and has no thumbprint.
    const hasAddress = !!(seller.address.trim() || seller.city.trim() || seller.zip.trim());
    const body = {
      kind: trade ? 'trade_in' : 'buy', requestId: requestId.current, customerId: known?.id ?? null, customer: { name: custName.trim(), phone: custPhone, email: trade ? email.trim() || null : null },
      brand, model: model.trim(), storage: storage || null, color: color.trim() || null, carrier, locked,
      imei: isImei ? digits : null, serial: !isImei ? code.trim() : null, condition, answers, offerCents: offer,
      payoutMethod: trade ? 'store_credit' : payout, payoutRef: !trade && isAppPayout(payout) ? payoutRef.trim() || null : null,
      seller: showId ? { name: seller.name.trim() || null, address: seller.address.trim() || null, city: seller.city.trim() || null, state: hasAddress ? seller.state.trim() || null : null, zip: seller.zip.trim() || null, dob: seller.dob || null } : null,
      id: idNo ? { type: idDoc.type, number: idNo, state: idDoc.state.trim() || null, expires: idDoc.expires || null } : null,
      signature: trade ? null : signature, ageConfirmed: showId && age,
      photos: photos.filter((p) => p.kind !== 'id' || showId), thumbprint: trade ? null : thumb, note: note.trim() || null,
    };
    const r = await run((approval?: ApprovalInput) => post('/api/buybacks', { ...body, approval }));
    if (r) {
      finished.current = true;
      clearDraft(trade);
      if (!(trade && forSale)) toast(`${r.number} saved. The phone is in inventory as Testing.`, 'good');
      // The customer goes on the next sale, so their trade-in comes off the phone they buy, and
      // the trade-in prints on that sale's receipt.
      if (trade) {
        const same = cart.cart.customer?.id === r.customer.id ? cart.cart.customer : null;
        cart.setCustomer({ ...same, id: r.customer.id, name: r.customer.name, phone: r.customer.phone, store_credit_cents: r.customer.store_credit_cents });
        cart.addTradeIn({ id: r.id, number: r.number, device: `${brand === 'Apple' ? '' : `${brand} `}${model.trim()}${storage ? ` ${storage}` : ''}`, imei: isImei ? digits : null, credit_cents: offer ?? 0 });
        if (forSale) {
          toast(`Trade-in ${r.number}: ${formatCents(offer ?? 0)} comes off the phone.`, 'good');
          navigate('/sale');
          return;
        }
      }
      // A trade-in's receipt (with the credit issued) prints right away.
      navigate(`/buybacks/${r.id}?saved=1${trade ? '&receipt=1' : ''}`);
    }
  };

  const amount = formatCents(offer ?? 0);
  const payButton = trade ? 'Accept Trade-In & Issue Credit' : payout === 'blue_bag' ? `Pay ${amount} from blue bag` : payout === 'cash' ? `Pay ${amount} cash from drawer` : `Save ${amount} ${PAYOUT_LABELS[payout]} payment`;
  const ans = (k: string, v: string) => setAnswers({ ...answers, [k]: v });
  // The ID on the form (scanned or typed): expired, or the seller is under 18.
  const today = todayIso();
  const idAge = showId && seller.dob ? ageOn(seller.dob, today) : null;
  const idWarning = !showId ? null
    : idDoc.expires && isExpired(idDoc.expires, today) ? `This ID expired on ${usDate(idDoc.expires)}. Ask for a current ID.`
    : !trade && idAge !== null && idAge >= 0 && idAge < 18 ? `The date of birth makes them ${idAge}. You can’t buy a phone from anyone under 18.`
    : null;
  return (
    <div className="page" style={{ paddingBottom: 120 }}>
      <div className="page-head">
        <h1>{trade ? 'Trade-in' : 'Buy a phone'}</h1>
        <div className="row-wrap">
          {draftHasContent(draft) ? <Button variant="ghost" icon="refresh" onClick={onStartOver}>Start over</Button> : null}
          {!trade ? <EasyBuyButton compact /> : null}
          <Button variant="ghost" icon="list" onClick={() => navigate('/buybacks')}>Phones bought</Button>
        </div>
      </div>
      {restoredAt ? (
        <div className="banner" role="status"><Icon.history /><div>Picked up where you left off. Everything filled in at {formatTime(restoredAt)} is still here. Tap <strong>Start over</strong> for an empty form.</div></div>
      ) : null}
      {!me?.register ? <div className="banner warn"><Icon.alert /><div>Buying phones needs the store tablet.</div></div> : null}
      {forSale ? <div className="banner"><Icon.swap /><div>This trade-in comes off the phone they’re buying. When you save it, you go back to the sale with the credit taken off.</div></div> : null}

      <section className="card stack">
        <div className="spread" style={{ flexWrap: 'wrap', rowGap: 4 }}>
          <h2 style={{ whiteSpace: 'nowrap' }}>1. Customer</h2>
          <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
            <Button size="sm" variant="ghost" icon="idCard" onClick={() => setScanOpen(true)}>Scan ID</Button>
          </div>
        </div>
        <div className="form-grid">
          <Field label="Customer name" hint="search the customers on file, or type a new one">
            <div className="cust-search">
              <input className="input input-lg" value={custName} onChange={(e) => { setCustName(e.target.value); setNameOpen(true); if (known && e.target.value.trim().toLowerCase() !== known.name.toLowerCase()) setKnown(null); }}
                onFocus={() => setNameOpen(true)} onBlur={() => setTimeout(() => setNameOpen(false), 150)} placeholder="Search customer name or phone" autoComplete="off" aria-label="Customer name" role="combobox" aria-expanded={nameOpen && nameHits.length > 0} />
              {nameOpen && nameHits.length ? (
                <div className="cust-drop" role="listbox" aria-label="Customers on file">
                  {nameHits.map((h) => (
                    <button key={h.id} type="button" role="option" aria-selected={false} className="cust-hit" onMouseDown={(e) => e.preventDefault()} onClick={() => chooseCustomer(h.id)}>
                      <span className="avatar avatar-sm">{h.name.slice(0, 1)}</span>
                      <span className="grow"><span className="t">{h.name}</span><span className="s">{[formatPhone(h.phone), h.email].filter(Boolean).join(' · ') || 'No contact info'}</span></span>
                      {h.store_credit_cents ? <span className="badge badge-green">Credit {formatCents(h.store_credit_cents)}</span> : null}
                    </button>
                  ))}
                  <div className="cust-none small muted">Not them? Keep typing: a new customer is added when you save.</div>
                </div>
              ) : null}
            </div>
          </Field>
          <Field label="Phone number" hint={custPhone.trim() && !phone10 ? 'needs 10 digits' : undefined}>
            <input className="input input-lg" inputMode="tel" value={custPhone} onChange={(e) => setCustPhone(e.target.value)} onBlur={() => phone10 && setCustPhone(formatPhone(phone10))} placeholder="(510) 555-0123" autoComplete="off" aria-label="Phone number" />
          </Field>
          {trade ? <Field label="Email" hint="optional"><input className="input" type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" /></Field> : null}
        </div>
        {scanned ? <LicenseSummary d={scanned} adult={!trade} /> : null}
        {known ? (
          <div className="customer-pill">
            <Icon.user />
            <div className="grow">
              <div className="strong">Returning customer: {known.name}</div>
              <div className="small muted">{formatPhone(known.phone)}{known.buybacks ? ` · sold us ${known.buybacks} phone${known.buybacks === 1 ? '' : 's'} before` : ''}{known.store_credit_cents ? ` · ${formatCents(known.store_credit_cents)} store credit` : ''}</div>
            </div>
            {custName.trim() && custName.trim().toLowerCase() !== known.name.toLowerCase() ? <Button size="sm" onClick={() => setCustName(known.name)}>Use {known.name}</Button> : null}
          </div>
        ) : phone10 && custName.trim() ? <p className="small muted" style={{ margin: 0 }}>New customer. They’re added when you save.</p> : null}
        {trade ? <p className="small muted" style={{ margin: 0 }}>That’s all a trade-in needs. The credit goes on their account.</p> : null}
        {!scanned && !custName.trim() ? <p className="small muted" style={{ margin: 0 }}>Scan the barcode on the back of their license to fill in their name, ID and address.</p> : null}
      </section>

      <section className="card stack">
        <h2>2. Phone</h2>
        <div className="chips" aria-label="Brand">
          {brands.map((b) => <button key={b} type="button" className="chip" aria-pressed={brand === b} onClick={() => { setBrand(b); setModel(''); setStorage(''); }}>{b}</button>)}
        </div>
        <Field label="IMEI (dial *#06#) or serial" hint={imeiBad ? 'That IMEI doesn’t add up. Check for a typo.' : 'scan or type it: the check runs and fills in the phone'}>
          <div className="imei-row">
            <input className="input input-lg grow" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={imeiKeys} aria-invalid={imeiBad} aria-label="IMEI or serial" autoComplete="off" />
            <Button variant="primary" icon="search" busy={checking} disabled={code.trim().length < 11 || imeiBad} onClick={() => checkImei()}>Check IMEI</Button>
            <PhoneCheckButton code={code} label="More checks" />
          </div>
        </Field>
        <p className="small muted" style={{ margin: 0 }}>
          {brand === 'Apple' ? 'Checks SICKW: model, carrier and SIM-lock, Find My (iCloud), blacklist and warranty.' : brand === 'Samsung' ? 'Checks SICKW: Samsung info (model, carrier, warranty) and the blacklist.' : `Checks SICKW for ${brand} phones.`}
          {' '}Not {/^[aeiou]/i.test(brand) ? 'an' : 'a'} {brand}? Pick the brand first.
        </p>
        {check ? <ImeiCheckView check={check} /> : null}
        {filled?.what.length ? (
          <div className="banner good" role="status" aria-label="Filled in from the IMEI check">
            <Icon.check /><div>Filled in from the IMEI check: <strong>{filled.what.join(' · ')}</strong>. Check it below and fix anything that’s wrong.</div>
          </div>
        ) : null}
        {filled?.otherBrand ? (
          <div className="suggest">
            <Icon.alert />
            <span className="grow">SICKW says this is {/^[aeiou]/i.test(filled.otherBrand) ? 'an' : 'a'} <strong>{filled.otherBrand}</strong> phone{filled.model ? ` (${filled.model})` : ''}, so the {brand} check doesn’t fit it. Nothing was filled in.</span>
            <Button size="sm" variant="primary" busy={checking} onClick={() => switchBrand(filled.otherBrand!)}>Check it as {filled.otherBrand}</Button>
          </div>
        ) : null}
        {check && check.status !== 'not_set_up' && check.checked_at ? (
          <div className="row-wrap">
            {sickwSays && differs && !filled?.otherBrand ? (
              <div className="suggest grow">
                <Icon.info />
                <span className="grow">SICKW says: <strong>{sickwSays.text}</strong></span>
                <Button size="sm" variant="primary" onClick={() => applySuggestion(sickwSays)}>Use this</Button>
              </div>
            ) : null}
            {check.cached ? <Button size="sm" variant="ghost" icon="refresh" busy={checking} onClick={() => checkImei({ fresh: true })}>Check again on SICKW</Button> : null}
          </div>
        ) : null}
        <div className="row" style={{ gap: 16, alignItems: 'flex-start' }}>
          <span className="thumb" style={{ width: 80, height: 80 }}><ModelPhoto brand={brand} model={model} color={color} size={80} /></span>
          <div className="grow stack-sm">
            <Field label="Model"><input className="input input-lg" value={model} onChange={(e) => { setModel(e.target.value); setStorage(''); }} placeholder={brand === 'Apple' ? 'e.g. iPhone 15' : 'Type the model'} aria-label="Model" /></Field>
            {suggestions.length ? <div className="chips" aria-label="Models">{suggestions.map((m) => <button key={m} type="button" className="chip" onClick={() => setModel(m)}>{m}</button>)}</div> : null}
          </div>
        </div>
        {storages.length ? (
          <div className="stack-sm"><span className="small strong">Storage</span><div className="chips">{storages.map((x) => <button key={x} type="button" className="chip" aria-pressed={storage === x} onClick={() => setStorage(x)}>{x}</button>)}</div></div>
        ) : null}
        <div className="stack-sm">
          <span className="small strong">Carrier</span>
          <div className="chips">{carriers.map((c: string) => <button key={c} type="button" className="chip" aria-pressed={carrier === c} onClick={() => { setCarrier(c); setLocked(c !== 'Unlocked'); setCarrierPicked(true); }}>{c}</button>)}</div>
          {carrier !== 'Unlocked' ? <label className="check"><input type="checkbox" checked={locked} onChange={(e) => { setLocked(e.target.checked); setCarrierPicked(true); }} /> Locked to {carrier}</label> : null}
        </div>
        <div className="form-grid">
          <Field label="Color"><input className="input" value={color} onChange={(e) => setColor(e.target.value)} /></Field>
          {/* A model the website has no sizes for: the storage is typed (or filled in by the check). */}
          {!storages.length && model.trim() ? <Field label="Storage"><input className="input" value={storage} onChange={(e) => setStorage(e.target.value)} placeholder="e.g. 128GB" aria-label="Storage" /></Field> : null}
        </div>
      </section>

      <section className="card stack">
        <h2>3. Grade and offer</h2>
        <div className="chips" role="group" aria-label="Grade">{(cat.data?.conditions ?? []).map((c: string) => <button key={c} type="button" className="chip grade-chip" aria-pressed={condition === c} title={cat.data?.grade_help?.[c]} onClick={() => setCondition(c)}>{c}</button>)}</div>
        {condition && cat.data?.grade_help?.[condition] ? <p className="small grade-help"><strong>{condition}:</strong> {cat.data.grade_help[condition]}</p> : <p className="small muted">Pick the grade: A is like new, D has a bad screen.</p>}
        {(cat.data?.questions ?? []).length ? (
          <div className="qa">
            {cat.data.questions.map((q: any) => (
              <div key={q.key} className="qa-row">
                <span className="small strong">{q.label}</span>
                <span className="chips">
                  {['Yes', 'No'].map((v) => <button key={v} type="button" className="chip" aria-pressed={answers[q.key] === v} aria-label={`${q.label} ${v}`} onClick={() => ans(q.key, v)}>{v}</button>)}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        <div className="row-wrap">
          <Button variant={est ? 'secondary' : 'primary'} busy={estimating} disabled={!model.trim() || !condition} onClick={() => { askedFor.current = offerKey; void estimate(); }}>{est ? 'Ask the website again' : 'Get the website offer'}</Button>
          {cat.data?.source === 'none' ? <span className="small muted">The Hub isn’t answering yet. It may be waking up.</span> : null}
        </div>
        {est ? (
          est.reached ? (
            est.eligible ? (
              <div className="offer-box"><div className="small muted">Website offer{est.website_condition && est.website_condition !== condition ? ` (website condition: ${est.website_condition})` : ''}</div><div className="big num">{formatCents(est.offer_cents)}</div>{est.disclaimer ? <div className="small muted">{est.disclaimer}</div> : null}</div>
            ) : <div className="banner warn"><Icon.alert /><div>{est.message || 'The website doesn’t buy this phone in this condition.'} A manager can still approve a price.</div></div>
          ) : <div className="banner warn"><Icon.alert /><div>{est.message}</div></div>
        ) : null}
        <Field label={trade ? 'Trade-in credit' : 'Amount you pay'} hint={est?.offer_cents ? `more than ${formatCents(est.offer_cents)} needs a manager` : estimating ? 'getting the website offer…' : est ? 'type it: a manager approves it' : 'the website offer fills this in'}>
          <MoneyInput value={offer} onChange={setOffer} />
        </Field>
      </section>

      <section className="card stack">
        {trade ? (
          <div className="spread" style={{ flexWrap: 'wrap', rowGap: 4 }}>
            <h2>4. ID (optional)</h2>
            <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
              <Button size="sm" variant="ghost" icon="idCard" onClick={() => setScanOpen(true)}>Scan ID</Button>
              <Button size="sm" variant="ghost" icon={showId ? 'close' : 'plus'} onClick={() => setShowId(!showId)}>{showId ? 'Don’t add ID' : 'Add their ID'}</Button>
            </div>
          </div>
        ) : (
          <div className="spread" style={{ flexWrap: 'wrap', rowGap: 4 }}>
            <h2>4. Seller’s ID</h2>
            <Button size="sm" variant="ghost" icon="idCard" onClick={() => setScanOpen(true)}>Scan ID</Button>
          </div>
        )}
        {trade ? (
          showId ? null : <p className="small muted" style={{ margin: 0 }}>Not needed for a trade-in. Add it only if you want it on the record.</p>
        ) : <p className="small muted" style={{ margin: 0 }}>California needs the seller’s name, address and ID for every phone you buy. Check the ID matches the person.</p>}
        {showId ? (
          <>
            <div className="chips" aria-label="ID type">{ID_TYPES.map((t) => <button key={t} type="button" className="chip" aria-pressed={idDoc.type === t} onClick={() => setIdDoc({ ...idDoc, type: t })}>{t}</button>)}</div>
            <div className="form-grid">
              <Field label="ID number"><input className="input" value={idDoc.number} onChange={(e) => setIdDoc({ ...idDoc, number: e.target.value.toUpperCase() })} autoComplete="off" /></Field>
              <div className="grid-2">
                <Field label="ID state"><input className="input" value={idDoc.state} maxLength={2} onChange={(e) => setIdDoc({ ...idDoc, state: e.target.value.toUpperCase() })} aria-label="ID state" /></Field>
                <Field label="Expires"><input className="input" type="date" value={idDoc.expires} onChange={(e) => setIdDoc({ ...idDoc, expires: e.target.value })} /></Field>
              </div>
              <Field label="Full name (as on the ID)"><input className="input" value={seller.name} placeholder={custName.trim() || undefined} onChange={(e) => setSeller({ ...seller, name: e.target.value })} /></Field>
              <Field label="Date of birth"><input className="input" type="date" value={seller.dob} onChange={(e) => setSeller({ ...seller, dob: e.target.value })} /></Field>
              <Field label="Street address" className="full"><input className="input" value={seller.address} onChange={(e) => setSeller({ ...seller, address: e.target.value })} /></Field>
              <Field label="City"><input className="input" value={seller.city} onChange={(e) => setSeller({ ...seller, city: e.target.value })} /></Field>
              <div className="grid-2">
                <Field label="State"><input className="input" value={seller.state} maxLength={2} onChange={(e) => setSeller({ ...seller, state: e.target.value.toUpperCase() })} /></Field>
                <Field label="ZIP"><input className="input" inputMode="numeric" value={seller.zip} onChange={(e) => setSeller({ ...seller, zip: e.target.value })} /></Field>
              </div>
            </div>
          </>
        ) : null}
        <div className="row-wrap">
          {showId ? <Button icon="plus" onClick={() => addPhoto('id')}>Photo of the ID</Button> : null}
          <Button icon="plus" onClick={() => addPhoto('device')}>Photo of the phone</Button>
          <span className="small muted">Kept with the record.</span>
        </div>
        {photos.length ? (
          <div className="photo-row">
            {photos.map((p, i) => (
              <span key={i} className="ph">
                <img src={p.dataUrl} alt={p.kind === 'id' ? 'ID' : 'Phone'} />
                <span className="tag">{p.kind === 'id' ? 'ID' : 'Phone'}</span>
                <button type="button" className="x" aria-label="Remove photo" onClick={() => setPhotos(photos.filter((_, j) => j !== i))}>×</button>
              </span>
            ))}
          </div>
        ) : null}
        {idWarning ? <div className="banner bad" role="alert"><Icon.alert /><div>{idWarning}</div></div> : null}
        {showId ? <label className="check"><input type="checkbox" checked={age} onChange={(e) => setAge(e.target.checked)} /> I checked the ID: it’s theirs and they’re 18 or older</label> : null}
      </section>

      <section className="card stack">
        <h2>5. {trade ? 'Accept this trade-in' : 'Payment'}</h2>
        {trade ? (
          <div className="accept-box" aria-label="Trade-in summary">
            <div className="ab-dev">
              <span className="thumb" style={{ width: 64, height: 64 }}><ModelPhoto brand={brand} model={model} color={color} size={64} /></span>
              <div className="grow">
                <div className="strong">{model.trim() ? `${brand === 'Apple' ? '' : `${brand} `}${model.trim()}${storage ? ` ${storage}` : ''}` : 'The phone'}</div>
                <div className="small muted">{[color.trim() || null, carrier !== 'Unlocked' ? `${carrier}${locked ? ' (locked)' : ''}` : 'Unlocked', condition ? `Grade ${condition}` : null, isImei ? `IMEI ${digits}` : code.trim() ? `S/N ${code.trim()}` : null].filter(Boolean).join(' · ')}</div>
              </div>
            </div>
            <div className="ab-val"><span>Trade-in value</span><span className="num">{formatCents(offer ?? 0)}</span></div>
            <p className="small" style={{ margin: 0 }}>When you accept, <strong>{formatCents(offer ?? 0)} store credit</strong> goes on {custName.trim() ? `${custName.trim()}’s` : 'their'} account right away (usable on anything they buy), the phone goes into inventory, and their trade-in receipt prints.</p>
          </div>
        ) : (
          <>
            <div className="chips" aria-label="Pay with">
              {BUY_PAYOUTS.map((v) => (
                <button key={v} type="button" className="chip" aria-pressed={payout === v} onClick={() => setPayout(v)}>{PAYOUT_LABELS[v]}</button>
              ))}
            </div>
            <p className={`small ${payout === 'cash' ? 'strong' : 'muted'}`} style={{ margin: 0 }}>{PAY_NOTE[payout]}</p>
            {isAppPayout(payout) ? (
              <Field label={payout === 'zelle' ? 'Zelle confirmation number' : payout === 'cashapp' ? 'Cash App payment ID' : 'Apple Pay note'}>
                <input className="input" value={payoutRef} onChange={(e) => setPayoutRef(e.target.value)} placeholder={payout === 'zelle' ? 'e.g. 8841 or 3:42 PM' : undefined} />
              </Field>
            ) : null}
          </>
        )}
        <Field label="Note"><input className="input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </section>

      {trade ? null : (
        <section className="card stack">
          <h2>6. Seller signs and thumbprint <span className="muted small">(optional)</span></h2>
          <p className="small">{me?.settings?.buyback_terms}</p>
          <SignaturePad onChange={setSignature} initial={signature} label="Seller signs here" />
          <div className="thumb-row">
            <div className="thumb-slot" aria-label="Thumbprint">{thumb ? <img src={thumb} alt="Seller’s right thumbprint" /> : <span className="stack-sm" style={{ alignItems: 'center' }}><Icon.fingerprint /><span>Right thumb</span></span>}</div>
            <div className="stack-sm grow">
              <div className="strong">Right thumbprint</div>
              <div className="small muted">The seller presses their right thumb flat on the fingerprint scanner, then you tap Scan.</div>
              <div className="row-wrap">
                <Button variant={thumb ? 'secondary' : 'primary'} icon="fingerprint" busy={scanning} onClick={scanThumb}>{thumb ? 'Scan again' : 'Scan thumbprint'}</Button>
                <Button variant="ghost" icon="plus" onClick={uploadThumb}>Upload scan</Button>
                {thumb ? <Button variant="ghost" onClick={() => setThumb(null)}>Clear</Button> : null}
              </div>
              {scanning ? <div className="small strong">Waiting for the thumb on the scanner…</div> : null}
              {scanErr ? <div className="error-text small" role="alert">{scanErr}</div> : null}
            </div>
          </div>
        </section>
      )}

      <div className="sticky-actions">
        <span className="grow small muted">{missing.length ? `Still needed: ${missing.join(', ')}` : trade ? <>Trade-in value <strong className="num" style={{ color: 'var(--navy)', fontSize: 18 }}>{formatCents(offer ?? 0)}</strong> · credit for {custName.trim()}</> : `${custName.trim()} · ${model} · ${formatCents(offer ?? 0)} · ${PAYOUT_LABELS[payout]}`}</span>
        <Button size="lg" variant="primary" icon="check" busy={busy} disabled={!!missing.length || !me?.register} onClick={save}>{payButton}</Button>
      </div>
      {scanOpen ? <ScanIdSheet onClose={() => setScanOpen(false)} onScan={applyLicense} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------- one purchase

/** One purchase: the record, photos, signature, thumbprint, the three printouts, and void. */
export function BuybackDetailScreen({ id, query }: { id: string; query: URLSearchParams }) {
  const { can, me } = useSession();
  const print = usePrint();
  const cloud = useCloudPrint();
  const cart = useCart();
  const toast = useToast();
  const { run, busy } = useAction();
  const b = useLoad<any>(() => get(`/api/buybacks/${id}`), [id]);
  const [voiding, setVoiding] = useState(false);
  const [stocking, setStocking] = useState(false);
  const [wholesaling, setWholesaling] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [reason, setReason] = useState('');
  const [justSaved] = useState(() => query.get('saved') === '1' || query.get('print') === '1');
  const [printReceipt] = useState(() => query.get('receipt') === '1');
  const cleaned = useRef(false);
  const printedReceipt = useRef(false);
  useEffect(() => {
    if (!cleaned.current && justSaved) {
      cleaned.current = true;
      history.replaceState(null, '', `#/buybacks/${id}`);
    }
  }, [id, justSaved]);
  const printHere = async (paper: PaperSize) => {
    try {
      const p = await get(`/api/buybacks/${id}/print`);
      if (paper === 'label' || paper === 'phone-label' || paper === 'phone-label-4') {
        const lp = labelPaper(me?.settings?.buyback_label_size);
        print(lp === 'label' ? <BuybackLabel p={p} /> : <BuybackBigLabel p={p} size={lp} />, lp);
        return;
      }
      print(paper === 'thermal' ? <BuybackReceiptPaper p={p} /> : <BuybackRecordLetter p={p} />, paper);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Couldn’t print.', 'error');
    }
  };
  // The customer's receipt prints on the store's receipt printer when it's set up (PrintNode);
  // the label and the store record print from this browser.
  const doPrint = (paper: PaperSize) => (paper === 'thermal'
    ? cloud.printOut({ kind: 'buyback_receipt', refId: id, title: `${b.data?.number ?? 'Buy back'} receipt`, browser: () => { printHere('thermal'); } })
    : printHere(paper));
  // Just accepted a trade-in: its receipt (with the credit issued) prints once.
  useEffect(() => {
    if (printReceipt && b.data && !printedReceipt.current) {
      printedReceipt.current = true;
      doPrint('thermal');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.data]);
  if (b.error) return <div className="page"><Empty icon="alert" title="This record couldn’t be loaded.">{b.error.message}</Empty></div>;
  if (!b.data) return <div className="page"><Spinner /></div>;
  const x = b.data;
  const voidIt = async () => {
    const r = await run(() => post(`/api/buybacks/${id}/void`, { reason }), { success: 'Voided.' });
    if (r) { setVoiding(false); b.setData(r); }
  };
  const useCredit = () => {
    cart.setCustomer({ id: x.customer.id, name: x.customer.name, phone: x.customer.phone, store_credit_cents: x.customer.store_credit_cents });
    if (x.kind === 'trade_in') cart.addTradeIn({ id: x.id, number: x.number, device: `${x.device.brand === 'Apple' ? '' : `${x.device.brand} `}${x.device.model}${x.device.storage ? ` ${x.device.storage}` : ''}`, imei: x.device.imei ?? null, credit_cents: x.offer_cents });
    navigate('/sale');
  };
  const prints: { paper: PaperSize; icon: 'printer' | 'tag' | 'bill'; title: string; sub: string }[] = [
    { paper: 'letter', icon: 'printer', title: 'Store record', sub: x.signature || x.id_doc.number || x.thumbprint?.taken ? 'Full page for your records, with the thumbprint box' : 'Full page for your records' },
    { paper: labelPaper(me?.settings?.buyback_label_size), icon: 'tag', title: 'Phone label', sub: `${labelPaper(me?.settings?.buyback_label_size) === 'label' ? '2.25 × 1.25 in sticker' : labelPaper(me?.settings?.buyback_label_size) === 'phone-label-4' ? '2.3 × 4 in label' : '2.4 × 3.9 in label (Brother)'} for the back of the phone: model, storage, carrier, IMEI, price paid. Scan it to open this record.` },
    { paper: 'thermal', icon: 'bill', title: 'Customer receipt', sub: 'Thermal receipt for the customer' },
  ];
  const voidText = x.payout_method === 'cash' ? 'the cash goes back in the drawer' : x.payout_method === 'blue_bag' ? 'the cash goes back in the blue bag' : x.payout_method === 'store_credit' ? 'store credit is taken back' : 'payment is marked returned';
  return (
    <div className="page">
      <div className="page-head">
        <span className="thumb" style={{ width: 64, height: 64 }}><ModelPhoto brand={x.device.brand} model={x.device.model} color={x.device.color} size={64} /></span>
        <div style={{ marginRight: 'auto' }}>
          <h1>{x.device.brand} {x.device.model}{x.device.storage ? ` ${x.device.storage}` : ''}</h1>
          <p className="muted">{x.number} · {x.kind === 'trade_in' ? 'Trade-in' : 'Buy back'} {formatDateTime(x.created_at)} · {x.location_name} · {x.employee_name}{x.approved_by_name ? ` (approved by ${x.approved_by_name})` : ''}</p>
        </div>
        {x.status === 'voided' ? <Badge tone="red">Voided</Badge> : <Badge tone="green" icon="check">{x.kind === 'trade_in' ? 'Trade-in done' : 'Buy back done'}</Badge>}
      </div>
      {x.status === 'voided' ? <div className="banner warn"><Icon.alert /><div>Voided {formatDateTime(x.voided_at)}: {x.void_reason}</div></div> : null}
      {justSaved && x.kind === 'trade_in' && x.status === 'completed' && x.customer.store_credit_cents > 0 ? (
        <div className="trade-prompt" role="region" aria-label="Sell a phone">
          <Icon.bag />
          <div className="grow"><div className="strong">Want to sell {x.customer.name.split(' ')[0] || 'them'} a phone now?</div><div className="small muted">Their {formatCents(x.customer.store_credit_cents)} credit comes off it, and this trade-in prints on the receipt.</div></div>
          <Button variant="primary" icon="bag" onClick={useCredit}>Sell a phone</Button>
        </div>
      ) : null}
      <section className={`card stack-sm ${justSaved ? 'saved-card' : ''}`}>
        {justSaved ? <div className="row"><Icon.check /><h2>{x.number} saved. Print:</h2></div> : <h3>Print</h3>}
        <div className="print-choices">
          {prints.map((p) => {
            const I = Icon[p.icon];
            return (
              <button key={p.paper} type="button" className="print-choice" onClick={() => doPrint(p.paper)}>
                <I /><span className="t">{p.title}</span><span className="s">{p.sub}</span>
              </button>
            );
          })}
        </div>
      </section>
      <div className="row-wrap">
        {!justSaved && x.kind === 'trade_in' && x.status === 'completed' && x.customer.store_credit_cents > 0 ? <Button variant="primary" icon="bag" onClick={useCredit}>Use {formatCents(x.customer.store_credit_cents)} credit on a sale</Button> : null}
        {x.device.id && x.status === 'completed' && (x.device.status === 'testing' || x.device.status === 'on_hold') ? <Button variant="primary" icon="boxes" onClick={() => setStocking(true)}>Add to inventory</Button> : null}
        {x.device.id && x.status === 'completed' && WHOLESALE_STATUSES.includes(x.device.status) ? <Button icon="truck" onClick={() => setWholesaling(true)}>Wholesale</Button> : null}
        {x.device.id ? <Button variant="ghost" icon="boxes" onClick={() => navigate(`/inventory/devices?id=${x.device.id}`)}>See it in inventory</Button> : null}
        {can('buyback.view_id') && x.status === 'completed' ? <Button variant="danger" onClick={() => setVoiding(true)}>Void</Button> : null}
      </div>
      <WholesaleInfo w={x.device.wholesale} onUndo={x.device.id && x.device.status === 'sold' ? () => setUndoing(true) : undefined} />
      <div className="grid-2" style={{ alignItems: 'start' }}>
        <section className="card stack-sm">
          <h3>Phone</h3>
          <dl className="kv">
            <dt>IMEI / serial</dt><dd className="num">{x.device.imei ?? x.device.serial}</dd>
            <dt>Carrier</dt><dd>{x.device.carrier}{x.device.locked ? ' (locked)' : ''}</dd>
            {x.device.color ? <><dt>Color</dt><dd>{x.device.color}</dd></> : null}
            <dt>Grade</dt><dd>{x.device.condition}</dd>
            {Object.entries(x.device.answers ?? {}).map(([k, v]) => <Fragment key={k}><dt>{k.replace(/_/g, ' ')}</dt><dd>{String(v)}</dd></Fragment>)}
            <dt>In inventory</dt><dd>{x.device.status ? (DEVICE_STATUS_LABELS[x.device.status] ?? x.device.status) : '—'}{x.hold_until ? ` · held until ${formatDateTime(x.hold_until)}` : ''}</dd>
          </dl>
          <h3 style={{ marginTop: 8 }}>IMEI check (SICKW)</h3>
          <ImeiCheckView check={x.imei_check} />
        </section>
        <section className="card stack-sm">
          <h3>Payment</h3>
          <dl className="kv">
            <dt>{x.kind === 'trade_in' ? 'Credit given' : 'Paid'}</dt><dd className="strong"><Money cents={x.offer_cents} /></dd>
            <dt>Website offer</dt><dd>{x.hub_offer_cents ? <Money cents={x.hub_offer_cents} /> : 'not available'}</dd>
            <dt>How</dt><dd>{x.payout_label ?? payoutLabel(x.payout_method, x.payout_ref)}</dd>
          </dl>
          <h3>Customer</h3>
          <dl className="kv">
            <dt>Name</dt><dd><a href={`#/customers/${x.customer.id}`}>{x.seller.name}</a></dd>
            <dt>Phone</dt><dd>{formatPhone(x.seller.phone ?? x.customer.phone) || '—'}</dd>
            {x.seller.address || x.seller.city ? <><dt>Address</dt><dd>{[x.seller.address, x.seller.city, x.seller.state, x.seller.zip].filter(Boolean).join(', ')}</dd></> : null}
            {x.id_doc.number || x.kind !== 'trade_in' ? <><dt>ID</dt><dd>{x.id_doc.number ? `${x.id_doc.type} ${x.id_doc.number}${x.id_doc.state ? ` (${x.id_doc.state})` : ''}${x.id_doc.expires ? ` · expires ${x.id_doc.expires}` : ''}` : 'Not taken'}{x.id_doc.checked ? ' · checked' : ''}</dd></> : null}
            {x.kind !== 'trade_in' ? <><dt>Thumbprint</dt><dd>{x.thumbprint?.taken ? 'Taken' : 'Not taken'}</dd></> : null}
          </dl>
          <div className="row-wrap" style={{ alignItems: 'flex-end' }}>
            {x.signature ? <img className="sig-img" src={x.signature} alt="Seller’s signature" /> : null}
            {x.thumbprint?.url ? <a className="thumb-slot small-slot" href={x.thumbprint.url} target="_blank" rel="noreferrer"><img src={x.thumbprint.url} alt="Seller’s right thumbprint" /></a> : null}
          </div>
        </section>
      </div>
      {x.note ? <p className="small"><strong>Note:</strong> {x.note}</p> : null}
      {x.photos.length ? (
        <section className="stack-sm">
          <h3>Photos</h3>
          <div className="photo-row">{x.photos.map((p: any) => <a key={p.id} className="ph" href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt={p.kind} /><span className="tag">{p.kind === 'id' ? 'ID' : 'Phone'}</span></a>)}</div>
        </section>
      ) : null}
      {stocking && x.device.id ? <StockPhoneSheet deviceId={x.device.id} onClose={() => setStocking(false)} onDone={() => { setStocking(false); b.reload(); }} /> : null}
      {wholesaling && x.device.id ? <WholesaleSheet deviceId={x.device.id} onClose={() => setWholesaling(false)} onDone={() => { setWholesaling(false); b.reload(); }} /> : null}
      {undoing && x.device.id && x.device.wholesale ? (
        <UndoWholesaleSheet
          deviceId={x.device.id} title={`${x.device.brand} ${x.device.model}${x.device.storage ? ` ${x.device.storage}` : ''}`} w={x.device.wholesale}
          onClose={() => setUndoing(false)} onDone={() => { setUndoing(false); b.reload(); }}
        />
      ) : null}
      {voiding ? (
        <Confirm title={`Void ${x.number}?`} confirmLabel="Void" danger busy={busy} onClose={() => setVoiding(false)} onConfirm={voidIt}>
          <p>The phone comes out of inventory and {voidText}. Only do this if the seller gave the money back.</p>
          <Field label="Reason"><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus /></Field>
        </Confirm>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- the list

/** All phones bought and trade-ins, today's money by how it was paid, and the police report for managers. */
/**
 * "Add to inventory": a phone we bought or took in trade goes on sale in one step. The price
 * starts at what the last one like it sold for; Enter adds it. Other roles need a manager's PIN.
 * Its price sticker prints right away (the selling price, battery health, IMEI…).
 */
export function StockPhoneSheet({ deviceId, onClose, onDone }: { deviceId: string; onClose: () => void; onDone: (device: any) => void }) {
  const { me } = useSession();
  const print = usePrint();
  const d = useLoad<any>(() => get(`/api/devices/${deviceId}`), [deviceId]);
  const { run, busy } = useAction();
  const [price, setPrice] = useState<number | null>(null);
  const [grade, setGrade] = useState('');
  const [battery, setBattery] = useState('');
  const filled = useRef(false);
  useEffect(() => {
    if (d.data && !filled.current) {
      filled.current = true;
      setPrice(d.data.price_cents || d.data.suggested_price_cents || null);
      setGrade(d.data.grade ?? '');
      setBattery(d.data.battery_health ? String(d.data.battery_health) : '');
    }
  }, [d.data]);
  const x = d.data;
  const held = !!x?.hold_until && x.hold_until > Date.now();
  const batteryBad = !!battery && (Number(battery) < 1 || Number(battery) > 100);
  const save = async () => {
    if (batteryBad) return;
    const r = await run(
      (approval?: ApprovalInput) => post(`/api/devices/${deviceId}/stock`, { priceCents: price, grade: grade || null, batteryHealth: battery ? Number(battery) : null, approval }),
      { success: held ? 'In inventory. It can be sold when the hold ends. Its price sticker is printing.' : 'In inventory and ready to sell. Its price sticker is printing.' },
    );
    if (r) {
      printPriceStickers(print, [r], me?.settings);
      onDone(r);
    }
  };
  return (
    <Sheet title="Add to inventory" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" icon="boxes" busy={busy} disabled={!price || !x || batteryBad} onClick={save}>Add to inventory</Button></>}>
      {d.error ? <p className="muted">{d.error.message}</p> : !x ? <Spinner /> : (
        <div className="stack-sm">
          <p className="muted">{x.title ?? `${x.brand} ${x.model}`} goes on sale at this price, without the testing checklist. Its price sticker prints right away.{held ? ` It can be sold after the hold ends, ${formatDateTime(x.hold_until)}.` : ''}</p>
          <Field label="Selling price" hint={x.suggested_price_cents ? `the last one like it: ${formatCents(x.suggested_price_cents)}` : 'what you’ll sell it for'}>
            <MoneyInput value={price} onChange={setPrice} autoFocus />
          </Field>
          <Field label="Battery health %" hint={batteryBad ? '1 to 100' : 'on the phone: Settings → Battery; prints on the sticker'}>
            <input className="input" inputMode="numeric" value={battery} onChange={(e) => setBattery(e.target.value.replace(/\D/g, '').slice(0, 3))} placeholder="e.g. 89" />
          </Field>
          <div className="stack-sm">
            <span className="small strong">Grade</span>
            <div className="chips" role="group" aria-label="Grade">
              {Object.entries(GRADE_LABELS).map(([k, v]) => <button key={k} type="button" className="chip" aria-pressed={grade === k} onClick={() => setGrade(grade === k ? '' : k)}>{k === 'parts' ? v : k}</button>)}
            </div>
          </div>
        </div>
      )}
    </Sheet>
  );
}

export function BuybacksScreen() {
  const { can } = useSession();
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const list = useLoad<any[]>(() => get(`/api/buybacks${qs({ q: dq, limit: 100 })}`), [dq]);
  const [days, setDays] = useState(7);
  // Tick phones (or Select all) and edit the phones in inventory behind them in one go (price, cost, status, grade).
  const [picked, setPicked] = useState<string[]>([]);
  const [editingMany, setEditingMany] = useState(false);
  const rows: any[] = list.data ?? [];
  const chosen = rows.filter((b) => picked.includes(b.id) && b.device_id && b.status !== 'voided' && b.device_status !== 'sold');
  const allPicked = rows.length > 0 && picked.length === rows.length;
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const canEditMany = can('device.edit_price') || can('inventory.adjust') || can('device.add');
  const reportUrl = `/api/buybacks/report.csv${qs({ from: Date.now() - days * 86_400_000, to: Date.now() + 60_000 })}`;
  const today = useMemo(() => {
    if (!list.data || dq) return null;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const rows = list.data.filter((b) => b.status === 'completed' && b.kind === 'buy' && b.created_at >= start.getTime());
    const by = new Map<string, { n: number; cents: number }>();
    for (const r of rows) {
      const t = by.get(r.payout_method) ?? { n: 0, cents: 0 };
      t.n++;
      t.cents += r.offer_cents;
      by.set(r.payout_method, t);
    }
    return { n: rows.length, cents: rows.reduce((s, r) => s + r.offer_cents, 0), by: [...by.entries()].sort((a, b) => b[1].cents - a[1].cents) };
  }, [list.data, dq]);
  return (
    <div className="page">
      <div className="page-head">
        <h1>Phones bought</h1>
        <Button variant="primary" icon="phoneCash" onClick={() => navigate('/buy')}>Buy a phone</Button>
        <EasyBuyButton compact />
        <Button icon="swap" onClick={() => navigate('/trade')}>Trade-in</Button>
      </div>
      {today && today.n ? (
        <section className="card card-tight row-wrap" aria-label="Bought today">
          <span className="strong">Today: {today.n} phone{today.n === 1 ? '' : 's'} · {formatCents(today.cents)}</span>
          {today.by.map(([m, t]) => <Badge key={m} tone={m === 'blue_bag' ? 'blue' : m === 'cash' ? 'amber' : 'plain'}>{PAYOUT_LABELS[m] ?? m}: {formatCents(t.cents)} ({t.n})</Badge>)}
        </section>
      ) : null}
      <div className="search"><Icon.search /><input className="input input-lg" placeholder="B-1001, seller name, phone, IMEI or model" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search phones bought" /></div>
      {canEditMany && rows.length ? (
        <div className="pick-bar" role="region" aria-label="Pick phones">
          <PickAll all={allPicked} picked={picked.length} total={rows.length} what="phones" onToggle={() => setPicked(allPicked ? [] : rows.map((b) => b.id))} />
          <span className="grow" />
          {picked.length ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setPicked([])}>Clear</Button>
              {chosen.length ? <Button size="sm" icon="edit" onClick={() => setEditingMany(true)}>Edit {chosen.length} phone{chosen.length === 1 ? '' : 's'}</Button> : <span className="small muted">Sold or voided phones can’t be edited</span>}
            </>
          ) : <span className="small muted">Tick phones to change their price, cost, status or grade together</span>}
        </div>
      ) : null}
      {!list.data ? <Spinner /> : list.data.length ? (
        <div className="list">
          {list.data.map((b) => (
            <div key={b.id} className="pc-row">
            {canEditMany ? <label className="pc-pick" aria-label={`Pick ${b.number}`}><input type="checkbox" checked={picked.includes(b.id)} onChange={() => toggle(b.id)} /></label> : null}
            <a className="list-row" href={`#/buybacks/${b.id}`}>
              <span className="thumb" style={{ width: 40, height: 40 }}><ModelPhoto brand={b.brand} model={b.model} size={40} /></span>
              <span className="main-col"><span className="t" style={{ display: 'block' }}>{b.number} · {b.brand} {b.model}{b.storage ? ` ${b.storage}` : ''}</span><span className="s" style={{ display: 'block' }}>{b.kind === 'trade_in' ? 'Trade-in' : 'Buy back'} from {b.seller_name} · {formatDateTime(b.created_at)} · {b.employee_name}{b.kind === 'buy' ? ` · ${PAYOUT_LABELS[b.payout_method] ?? 'Other'}` : ''}</span></span>
              {b.status === 'voided' ? <Badge tone="red">Voided</Badge> : b.device_status === 'testing' ? <Badge tone="amber">Testing</Badge> : null}
              <Money cents={b.offer_cents} className="strong" />
            </a>
            </div>
          ))}
        </div>
      ) : <Empty icon="phoneCash" title={q ? 'Nothing matches.' : 'No phones bought yet.'} />}
      {editingMany ? <EditManySheet devices={chosen.map((b) => ({ id: b.device_id, title: `${b.number} · ${b.brand} ${b.model}${b.storage ? ` ${b.storage}` : ''}`, price_cents: b.device_price_cents ?? 0 }))} onClose={() => setEditingMany(false)} onDone={() => { setEditingMany(false); setPicked([]); list.reload(); }} /> : null}
      {can('buyback.view_id') ? (
        <section className="card stack-sm" style={{ marginTop: 16 }}>
          <h3>Police report (CAPSS)</h3>
          <p className="small muted">A spreadsheet of every purchase with the seller’s ID and the IMEI, for your secondhand dealer reporting.</p>
          <div className="row-wrap">
            {[1, 7, 30].map((d) => <button key={d} type="button" className="chip" aria-pressed={days === d} onClick={() => setDays(d)}>{d === 1 ? 'Today' : `Last ${d} days`}</button>)}
            <a className="btn btn-secondary" href={reportUrl} download="buybacks.csv">Download</a>
          </div>
        </section>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- printouts

interface BuybackPrint {
  business_name: string; logo_url: string; website: string; terms: string; store: StoreInfo; policies?: string | null;
  number: string; kind: 'buy' | 'trade_in'; status: string; created_at: number; employee_name: string; location_name: string; approved_by_name: string | null;
  customer: { name: string; phone: string | null; store_credit_cents: number };
  device: { brand: string; model: string; storage: string | null; color: string | null; carrier: string | null; locked: boolean; imei: string | null; serial: string | null; condition: string; answers: Record<string, string> };
  imei_check: { status: string; text: string | null; checked_at: number | null; sections?: ImeiCheckSection[] };
  /** Null for anyone but the owner once the day it was bought is over. */
  offer_cents: number | null; payout_method: string; payout_ref: string | null; hold_until: number | null; note: string | null;
  seller: { name: string; phone: string | null; address: string | null; city: string | null; state: string | null; zip: string | null };
  id_doc: { type: string; state: string | null; expires: string | null; checked?: boolean };
  record_id_number: string; record_dob: string | null; id_number_masked: string; signature: string | null; record_thumbprint: string | null;
}

const shortDate = (ms: number) => new Intl.DateTimeFormat('en-US', { timeZone: zone.current, month: '2-digit', day: '2-digit', year: '2-digit' }).format(new Date(ms));
const checkLabel = (s: string) => CHECK[s]?.label ?? s.replace('_', ' ');
const deviceName = (d: BuybackPrint['device']) => `${d.brand} ${d.model}${d.storage ? ` ${d.storage}` : ''}`;
export const carrierText = (d: Pick<BuybackPrint['device'], 'carrier' | 'locked'>) => (d.carrier && d.carrier !== 'Unlocked' ? `${d.carrier}${d.locked ? ' locked' : ''}` : 'Unlocked');

/** The seller's receipt (80 mm thermal). */
export function BuybackReceiptPaper({ p }: { p: BuybackPrint }) {
  const phone = formatPhone(p.seller.phone ?? p.customer.phone);
  return (
    <div className="receipt">
      <ReceiptHeader logo={p.logo_url} business={p.business_name} store={p.store} website={p.website} />
      <div className="c strong">{buybackReceiptTitle(p.kind)}</div>
      <div className="ticket-no">{p.number}</div>
      <div className="bar"><Barcode value={p.number} /></div>
      <dl className="meta">
        <dt>Date</dt><dd>{formatDateTime(p.created_at)}</dd>
        <dt>Customer</dt><dd>{p.seller.name}</dd>
        {phone ? <><dt>Phone</dt><dd>{phone}</dd></> : null}
        {p.record_id_number ? <><dt>ID</dt><dd>{p.id_doc.type} {p.id_number_masked}</dd></> : null}
        <dt>Served by</dt><dd>{p.employee_name}</dd>
      </dl>
      <hr />
      <div className="strong">{deviceName(p.device)}</div>
      <div>{[p.device.color, carrierText(p.device)].filter(Boolean).join(' · ')}</div>
      <div>{p.device.imei ? `IMEI ${p.device.imei}` : `Serial ${p.device.serial}`}</div>
      <div className="dim">Grade: {p.device.condition}</div>
      <hr />
      <div className="rl tot"><span>{p.kind === 'trade_in' ? 'CREDIT ISSUED' : 'PAID TO YOU'}</span><span>{p.offer_cents === null ? '-' : formatCents(p.offer_cents)}</span></div>
      <div className="rl"><span>Paid by</span><span>{sellerPayoutLabel(p.payout_method, p.payout_ref)}</span></div>
      {p.payout_method === 'store_credit' ? <div className="rl"><span>Store credit balance</span><span>{formatCents(p.customer.store_credit_cents)}</span></div> : null}
      <hr />
      {/* A trade-in isn't signed: no seller's statement or signature line. */}
      {p.signature ? (
        <>
          <div className="terms">{p.terms}</div>
          <img className="sig-print" src={p.signature} alt="Seller’s signature" />
          <div className="sign">Seller signature</div>
        </>
      ) : null}
      <div className="c strong pickup-note">Thank you! Keep this receipt.</div>
      <div className="c dim">Scan its barcode in STORE OS to open this record.</div>
      <ReceiptPolicies text={p.policies} />
      <SiteLine website={p.website} />
      <ReceiptMark />
    </div>
  );
}

/** The paper for the phone's label in the size picked in Settings → Buy & trade-in. */
export function labelPaper(size: string | undefined): PaperSize {
  return size === 'dymo_30334' ? 'label' : size === 'dymo_30256' ? 'phone-label-4' : 'phone-label';
}

/**
 * The label for the back of a bought phone, big enough to read across the counter: the record
 * number and its barcode (scan it to open this record), the model and storage, the carrier, the
 * IMEI with its own barcode, what we paid, and the hold date. Brother 62 × 100 mm or DYMO 30256.
 */
export function BuybackBigLabel({ p, size }: { p: BuybackPrint; size: 'phone-label' | 'phone-label-4' }) {
  const d = p.device;
  const sections = p.imei_check.sections ?? [];
  const icloud = findLine(sections, /icloud|find my|fmi/i)?.[1];
  const model = d.brand === 'Apple' ? d.model : `${d.brand} ${d.model}`;
  return (
    <div className={`plabel-big ${size === 'phone-label-4' ? 'd30256' : ''}`}>
      <div className="pb-kind">{p.kind === 'trade_in' ? 'TRADE-IN' : 'BUY BACK'}</div>
      <div className="pb-top"><span>{p.business_name}</span><span>{shortDate(p.created_at)}</span></div>
      <div className="pb-no">{p.number}</div>
      <div className="pb-bar"><Barcode value={p.number} height={40} /></div>
      <hr />
      <div className="pb-model">{model}</div>
      <div className="pb-spec">{d.storage ? <span className="pb-gb">{d.storage}</span> : null}{d.color ? <span>{d.color}</span> : null}</div>
      <div className="pb-carrier">{carrierText(d)}</div>
      <hr />
      <div className="pb-imei-l">{d.imei ? 'IMEI' : 'SERIAL'}</div>
      <div className="pb-imei">{d.imei ?? d.serial}</div>
      {d.imei || d.serial ? <div className="pb-imei-bar"><Barcode value={(d.imei ?? d.serial) as string} height={36} /></div> : null}
      <hr />
      <div className="pb-paid"><span>{p.kind === 'trade_in' ? 'CREDIT' : 'PAID'}</span><span className="amt">{p.offer_cents === null ? '-' : formatCents(p.offer_cents)}</span></div>
      <div className="pb-sub">{[`Grade ${d.condition}`, p.kind === 'trade_in' ? null : PAYOUT_LABELS[p.payout_method]].filter(Boolean).join(' · ')}</div>
      <div className="pb-hold">{p.hold_until ? `HOLD until ${shortDate(p.hold_until)}` : 'No hold'}</div>
      <div className="pb-foot">IMEI check: {checkLabel(p.imei_check.status)}{icloud ? ` · iCloud ${icloud}` : ''}</div>
    </div>
  );
}

/** The small sticker for the back of the phone (2.25 × 1.25 in). Scanning its barcode opens this record. */
export function BuybackLabel({ p }: { p: BuybackPrint }) {
  const d = p.device;
  return (
    <div className="plabel">
      <div className="pl-top"><span className="pl-no">{p.number}</span><span><b className="pl-kind">{p.kind === 'trade_in' ? 'TRADE-IN' : 'BUY BACK'}</b> {shortDate(p.created_at)}</span></div>
      <div className="pl-dev">{d.brand === 'Apple' ? '' : `${d.brand} `}{d.model}{d.storage ? ` ${d.storage}` : ''}</div>
      <div className="pl-ln">{d.imei ? `IMEI ${d.imei}` : `S/N ${d.serial}`}</div>
      <div className="pl-ln">{[d.color, carrierText(d), `Grade ${d.condition}`].filter(Boolean).join(' · ')}</div>
      <div className="pl-ln"><strong>{p.hold_until ? `HOLD until ${shortDate(p.hold_until)}` : 'No hold'}</strong>{` · IMEI ${checkLabel(p.imei_check.status).toLowerCase()}`}</div>
      <div className="pl-bc"><Barcode value={p.number} height={30} /></div>
    </div>
  );
}

/** The store's full-page purchase record: seller, ID, phone, every SICKW line, signature and thumbprint. */
export function BuybackRecordLetter({ p }: { p: BuybackPrint }) {
  const sections = p.imei_check.sections ?? [];
  const phone = formatPhone(p.seller.phone ?? p.customer.phone);
  // A trade-in without an ID or signature has no ID block, statement or thumbprint box.
  const fullRecord = !!(p.signature || p.record_id_number || p.record_thumbprint);
  return (
    <div className="invoice">
      <div className="ih">
        <div>
          {p.logo_url ? <img src={p.logo_url} alt={p.business_name} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} /> : null}
          <div className="biz">{p.business_name}</div>
          <div>{p.store.name} store</div>
          {storeLines(p.store).map((l) => <div key={l}>{l}</div>)}
          <div>{[p.store.phone ? formatPhone(p.store.phone) : null, p.website].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="right">
          <div className="kind-banner">{p.kind === 'trade_in' ? 'TRADE-IN' : 'BUY BACK'}</div>
          <h1>{p.kind === 'trade_in' ? 'TRADE-IN RECORD' : 'BUY BACK RECORD'}</h1>
          <div className="strong" style={{ fontSize: 17 }}>{p.number}</div>
          <div>{formatDateTime(p.created_at)}</div>
          <div className="barc"><Barcode value={p.number} height={40} /></div>
        </div>
      </div>
      <div className="two">
        <div>
          <div className="lab">{p.kind === 'trade_in' ? 'Customer' : 'Seller'}</div>
          <div className="strong">{p.seller.name}</div>
          {phone ? <div>{phone}</div> : null}
          {p.seller.address ? <div>{p.seller.address}</div> : null}
          {p.seller.city || p.seller.zip ? <div>{[p.seller.city, p.seller.state].filter(Boolean).join(', ')} {p.seller.zip}</div> : null}
          {p.record_dob ? <div>Born {p.record_dob}</div> : null}
        </div>
        {p.record_id_number || p.kind !== 'trade_in' ? (
          <div>
            <div className="lab">Identification</div>
            {p.record_id_number ? (
              <>
                <div className="strong">{p.id_doc.type}</div>
                <div>Number {p.record_id_number}{p.id_doc.state ? ` · ${p.id_doc.state}` : ''}</div>
                {p.id_doc.expires ? <div>Expires {p.id_doc.expires}</div> : null}
              </>
            ) : <div>No ID recorded</div>}
            <div>Checked by employee: {p.id_doc.checked ? 'Yes' : 'No'}</div>
          </div>
        ) : null}
      </div>
      <table>
        <thead><tr><th>Item</th><th>IMEI / serial</th><th>Grade</th><th className="r">{p.kind === 'trade_in' ? 'Credit' : 'Paid'}</th></tr></thead>
        <tbody>
          <tr>
            <td className="strong">{deviceName(p.device)}<div style={{ color: '#555', fontWeight: 400 }}>{[p.device.color, carrierText(p.device)].filter(Boolean).join(' · ')}</div></td>
            <td className="num">{p.device.imei ?? p.device.serial}</td>
            <td>{p.device.condition}</td>
            <td className="r">{p.offer_cents === null ? '-' : formatCents(p.offer_cents)}</td>
          </tr>
        </tbody>
      </table>
      <div className="totals">
        <div><span>Paid by</span><span>{payoutLabel(p.payout_method, p.payout_ref)}</span></div>
        <div><span>IMEI check</span><span>{checkLabel(p.imei_check.status)}</span></div>
        {p.hold_until ? <div><span>Hold until</span><span>{formatDateTime(p.hold_until)}</span></div> : null}
        <div><span>Employee</span><span>{p.employee_name}</span></div>
        {p.approved_by_name ? <div><span>Approved by</span><span>{p.approved_by_name}</span></div> : null}
      </div>
      {sections.length ? (
        <div className="imei-print">
          <div className="lab">IMEI check (SICKW){p.imei_check.checked_at ? ` · ${formatDateTime(p.imei_check.checked_at)}` : ''}</div>
          {sections.map((s) => (
            <div key={s.service}>
              <div className="strong">{s.title}</div>
              {s.ok ? <dl>{s.fields.map(([k, v], i) => <Fragment key={i}><dt>{k}</dt><dd>{v}</dd></Fragment>)}</dl> : <div>{s.error}</div>}
            </div>
          ))}
        </div>
      ) : null}
      {p.note ? <div style={{ marginTop: 10 }}><strong>Note:</strong> {p.note}</div> : null}
      {fullRecord ? (
        <>
          <div className="terms">{p.terms}</div>
          <div className="record-sign">
            <div className="sigblock">{p.signature ? <img src={p.signature} alt="Seller’s signature" /> : null}<div className="line">Seller signature</div></div>
            <div className="thumb-box">{p.record_thumbprint ? <div className="ti"><img src={p.record_thumbprint} alt="Seller’s right thumbprint" /></div> : null}<span>Right thumbprint</span></div>
          </div>
        </>
      ) : null}
      <SiteLine website={p.website} />
      <div className="foot">Store copy · keep for your records</div>
      <ReceiptMark />
    </div>
  );
}
